import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Response } from '@shared/models/response.model';
import { Project } from '@shared/models/project.model';
import { map } from 'rxjs/operators';
import { BaseHttpService } from '@shared/services/base-http.service';
import { TableResponse } from '@shared/models/table-response.model';
import { NoteData } from '@shared/interfaces/project/note-data.interface';
import { NotePack } from '@shared/interfaces/project/note-pack.interface';
import { PayStatistic } from '@shared/interfaces/project/pay-statistic.interface';
import { Contributor } from '@shared/interfaces/project/contributor.interface';
import { CreatedReport } from '@shared/interfaces/project/created-report.interface';
import { Tag } from '@shared/interfaces/project/tag.interface';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  constructor(public baseHttp: BaseHttpService) {}
  private currentProjectCategories: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(null);
  public currentProjectCategories$: Observable<any[]> = this.currentProjectCategories.asObservable();

  getProjects(opt?): Observable<Response<TableResponse<Project[]>>> {
    return this.baseHttp
      .get('/project', opt)
      .pipe(map((resp) => resp as Response<TableResponse<Project[]>>));
  }

  getProjectsWithPath(path: string, opt?): Observable<Response<TableResponse<Project[]>>> {
    return this.baseHttp
      .get('/project/' + path, opt)
      .pipe(map((resp) => resp as Response<TableResponse<Project[]>>));
  }

  getProject(id: number): Observable<Response<Project>> {
    return this.baseHttp.get('/project/' + id).pipe(map((resp) => resp as Response<Project>));
  }

  getProjectCategories(id: number): Observable<Response<Tag[]>> {
    return this.baseHttp
      .get('/project/' + id + '/category')
      .pipe(map((resp) => resp as Response<Tag[]>));
  }

  createNote(pack: NotePack): Observable<Response<NoteData>> {
    return this.baseHttp
      .put('/notes/' + pack.bagId, {
        note: pack.note,
        rate: pack.rate,
      })
      .pipe(map((resp) => resp as Response<NoteData>));
  }

  paymentOperators(projectId): Observable<Response<[]>> {
    return this.baseHttp
      .get(`/payments-operators/projects/${projectId}/available`)
      .pipe(map((resp) => resp as Response<[]>));
  }

  getNote(container: string, id: number, params: any): Observable<Response<NoteData>> {
    return this.baseHttp
      .get('/notes/' + container + '/' + id, params)
      .pipe(map((resp) => resp as Response<NoteData>));
  }

  deleteNote(id: number): Observable<Response<NoteData>> {
    return this.baseHttp.delete('/notes/' + id).pipe(map((resp) => resp as Response<NoteData>));
  }

  editNote(pack: NotePack): Observable<Response<NoteData>> {
    return this.baseHttp
      .post('/notes/' + pack.bagId, { note: pack.note })
      .pipe(map((resp) => resp as Response<NoteData>));
  }

  getContributorsList(
    id: number,
    page: number = 0
  ): Observable<Response<TableResponse<Contributor[]>>> {
    return this.baseHttp
      .get('/project/contributors-list/' + id + '?page=' + page)
      .pipe(map((resp) => resp as Response<TableResponse<Contributor[]>>));
  }

  getProjectPayingStatistics(id: number): Observable<Response<PayStatistic[]>> {
    return this.baseHttp
      .get('/project/project-payin-statisitcs/' + id)
      .pipe(map((resp) => resp as Response<PayStatistic[]>));
  }

  createReport(pack): Observable<Response<CreatedReport>> {
    return this.baseHttp
      .put('/project/report/' + pack.id, { report: { note: pack.note, title: pack.title } })
      .pipe(map((resp) => resp as Response<CreatedReport>));
  }

  endProject(id): Observable<Response<Project>> {
    return this.baseHttp
      .post('/project/close-project/' + id, {})
      .pipe(map((resp) => resp as Response<Project>));
  }

  deleteDraftProject(id): Observable<Response<Project>> {
    return this.baseHttp
      .delete('/project/delete-draft/' + id)
      .pipe(map((resp) => resp as Response<Project>));
  }

  projectList(id): Observable<Response<Project[]>> {
    return this.baseHttp
      .get('/project/list-on-profile/' + id)
      .pipe(map((resp) => resp as Response<Project[]>));
  }

  projectsSlider(userId: number, currentProjectId: number): Observable<Response<Project[]>> {
    return this.baseHttp
        .get('/project/slider-feed/' + userId + '?currentProjectId=' + currentProjectId)
        .pipe(map((resp) => resp as Response<Project[]>));
  }

  updateCurrentProjectCategories(categories) {
    this.currentProjectCategories.next(categories);
  }
}
